<template>
  <b-modal
    id="update-modal"
    no-close-on-backdrop
    ok-title="確認"
    centered
    header-bg-variant="primary"
    ok-only
    @hidden="handleHide"
    @close="handleHide"
  >
    <template #modal-title>
      <h4 class="m-0 model-header">
        {{ classDataItem.id ? '編輯分類' : '新增分類' }}
      </h4>
    </template>

    <validation-observer
      #default="{ handleSubmit }"
      ref="refFormObserver"
    >
      <b-form
        @submit.prevent="handleSubmit(onSubmit)"
        @reset.prevent="resetForm"
      >
        <!-- 部門 -->
        <b-form-group
          v-if="!classDataItem.id"
          label-for="department_id"
        >
          <template #label>
            <div class="d-flex justify-content-between align-items-center">
              <label class="mb-0">
                部門
                <span class="text-danger">*</span>
              </label>

              <span>
                <div
                  v-b-tooltip.hover.v-secondary
                  title="部門設定"
                  class="d-flex align-items-center actions-link-btn-sm"
                  @click="() => { $refs.departmentExplorerModal.getData(null) }"
                >
                  <b-img
                    src="/admin/images/table/settings.svg"
                    class="actions-link-btn-image"
                    rounded
                  />
                </div>
              </span>
            </div>
          </template>

          <v-select
            v-model="classData.department_id"
            :options="departmentsOptions"
            :clearable="false"
            label="name"
            :reduce="option => option.id"
            placeholder="請選擇部門"
          >
            <template v-slot:no-options="{ search, searching }">
              <template v-if="searching">
                沒有<em>{{ search }}</em> 的相關結果
              </template>

              <div v-else>
                <small>暫無資料</small>
              </div>
            </template>
          </v-select>
        </b-form-group>

        <!-- 名稱 -->
        <validation-provider
          #default="{ errors }"
          name="名稱"
          rules="required"
        >
          <b-form-group label-for="name">
            <template #label>
              <label class="mb-0">
                名稱
                <span class="text-danger">*</span>
              </label>
            </template>
            <b-form-input
              id="name"
              v-model="classData.name"
              :state="errors.length > 0 ? false : null"
              trim
              :placeholder="classDataItem.name ? classDataItem.name : '請輸入名稱'"
            />

            <b-form-invalid-feedback>
              {{ errors[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>

        <!-- 分類說明 -->
        <div>
          <label>分類說明</label>
          <b-form-textarea
            v-model="classData.description"
            :placeholder="classDataItem.description ? classDataItem.description : '請輸入作業分類說明'"
            :state="classData.description.length <= maxChar ? null : false"
            rows="5"
          />
          <small
            class="textarea-counter-value float-right"
            :class="classData.description.length > maxChar ? 'bg-danger' : ''"
          >
            <span class="char-count">{{ classData.description.length }}</span> / {{ maxChar }}
          </small>
        </div>
      </b-form>
    </validation-observer>

    <template #modal-footer>
      <div class="w-100 text-right">
        <b-button
          variant="primary"
          :disabled="isBusy"
          @click="handleOk"
        >
          <span v-if="!isBusy">確認</span>

          <b-spinner
            v-else
            small
          />
        </b-button>
      </div>
    </template>

    <department-explorer-modal
      ref="departmentExplorerModal"
      department-explorer-id="departmentExplorerModal"
      :is-hiden-emit="true"
      :use-callback-data="false"
      @emit-hidden-function="resetMetaList"
    />
    <!-- @call-back-data="(item) => callbackData(item)" -->

  </b-modal>
</template>

<script>
// API
import { ref } from '@vue/composition-api'
import { required, password } from '@validations'
import { ValidationProvider, ValidationObserver, localize } from 'vee-validate'

// UI
import {
  BForm, VBToggle, BButton, BSpinner, BFormInvalidFeedback, BFormGroup, BFormInput, BFormTextarea,
  BImg, VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
// import VueSlider from 'vue-slider-component'
import language from 'flatpickr/dist/l10n/zh-tw'
// import flatPickr from 'vue-flatpickr-component'
import Ripple from 'vue-ripple-directive'

// Component
import formValidation from '@core/comp-functions/forms/form-validation'
import DepartmentExplorerModal from '@/layouts/components/Modal/department-modal/departmentModal.vue'
import { useTodoClassList, useTodoSetting } from '../useTodo'
import { useAlert } from '@/libs/mixins/index'

export default {
  components: {
    BImg,
    BForm,
    // BFormGroup,
    BButton,
    BSpinner,
    BFormInvalidFeedback,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    // BFormSpinbutton,

    vSelect,
    // VueSlider,
    // flatPickr,
    ValidationProvider,
    ValidationObserver,
    DepartmentExplorerModal,
  },
  directives: {
    Ripple,
    'b-toggle': VBToggle,
    'b-tooltip': VBTooltip,
  },
  props: {
    classDataItem: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      required,
      password,
      missingFields: [],
      maxChar: 255,
    }
  },
  methods: {
    // 按下彈窗關閉
    handleHide(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.reseTodoTypeData()
      this.isBusy = false
      this.$nextTick(() => {
        this.$bvModal.hide('update-modal')
      })
    },

    // 按下彈窗確認
    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault()

      if (this.classData.description.length > this.maxChar) {
        this.useAlertToast(false, '作業分類說明字數超過上限')
        return
      }

      // 判斷必填欄位是否填寫
      const requiredFields = {
        name: '名稱',
      }

      this.missingFields = []

      for (let i = 0; i < Object.keys(requiredFields).length; i += 1) {
        if (this.classData[Object.keys(requiredFields)[i]] === null || this.classData[Object.keys(requiredFields)[i]] === '') {
          this.missingFields.push({
            lable: Object.values(requiredFields)[i],
            value: Object.keys(requiredFields)[i],
          })
        }
      }

      if (this.missingFields.length > 0) {
        this.useAlertToast(false, `${this.missingFields[0].lable}不得為空`)
        return
      }

      this.isBusy = true

      if (this.classDataItem.id) {
        this.setTodoClassUpdate({
          type_id: this.classDataItem.id,
          data: {
            ...this.classData,
          },
        })
          .then(response => {
            this.useHttpCodeAlert(response)
            this.$emit('refetch-data')
            this.reseTodoTypeData()
            this.$nextTick(() => {
              this.$bvModal.hide('update-modal')
            })
            this.isBusy = false
          })
          .catch(error => {
            this.useHttpCodeAlert(error.response)
            this.isBusy = false
          })
      } else {
        this.setTodoClassCreate({
          ...this.classData,
        })
          .then(response => {
            this.useHttpCodeAlert(response)
            this.$emit('refetch-data')
            this.reseTodoTypeData()
            this.$nextTick(() => {
              this.$bvModal.hide('update-modal')
            })
            this.isBusy = false
          })
          .catch(error => {
            this.useHttpCodeAlert(error.response)
            this.isBusy = false
          })
      }
    },

    // (重新)獲取Meta
    resetMetaList() {
      this.getMetaListData()
        .then(response => {
          const { departments } = response.data.data
          this.departmentsOptions = departments
          const findObject = departments.find(el => el.id === this.classDataItem.department_id)

          if (findObject === undefined) {
            this.classData.department_id = null
          }
        })
    },

    // (獲取)資料
    getData() {
      this.getMetaListData()
        .then(response => {
          const { departments } = response.data.data
          this.departmentsOptions = departments

          const resolveData = {
            ...this.syncObject(this.blankClassData, this.classDataItem),
          }

          const findObject = departments.find(el => el.id === this.classDataItem.department_id)

          if (findObject === undefined) {
            resolveData.department_id = null
          }
          this.classData = resolveData
          this.$bvModal.show('update-modal')
        })
        .catch(() => {
          const resolveData = {
            ...this.syncObject(this.blankClassData, this.classDataItem),
          }
          this.classData = resolveData
          this.$bvModal.show('update-modal')
        })
    },
  },
  setup() {
    localize('tw')

    const {
      isBusy,
      setTodoClassCreate,
      setTodoClassUpdate,
    } = useTodoClassList()

    const {
      blankClassData,
      getMetaListData,
      departmentsOptions,
      syncObject,
    } = useTodoSetting()

    const {
      useAlertToast,
      useHttpCodeAlert,
    } = useAlert()

    const classData = ref(JSON.parse(JSON.stringify(blankClassData)))

    const reseTodoTypeData = () => {
      classData.value = JSON.parse(JSON.stringify(blankClassData))
      isBusy.value = false
    }

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(reseTodoTypeData)

    const dateConfig = {
      locale: language.zh_tw,
      dateFormat: 'Y-m-d',
      allowInput: true,
      static: true,
    }

    return {
      isBusy,
      dateConfig,

      classData,
      departmentsOptions,
      blankClassData,
      setTodoClassCreate,
      setTodoClassUpdate,
      getMetaListData,

      reseTodoTypeData,
      refFormObserver,
      getValidationState,
      resetForm,

      useHttpCodeAlert,
      useAlertToast,
      syncObject,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '@core/scss/vue/libs/vue-slider.scss';
</style>

<style lang="scss" scoped>
.model-header {
  color: white;
  padding: 5px 0;
}
</style>
